import React, { useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { isValidDutchHouseNumber, isValidDutchPostalCode, lookupDutchAddress } from "../services/dutchAddressLookup";

type AddressField = "address" | "zipCode" | "city";

interface DutchAddressFieldsProps {
  address: string;
  zipCode: string;
  city: string;
  onChange: (field: AddressField, value: string) => void;
  onAddressFound?: (address: { address: string; zipCode: string; city: string }) => void;
  t: TFunction;
  inputClassName: string;
  labelClassName: string;
  fieldClassName?: string;
  addressClassName?: string;
}

export const splitStreetAddress = (address = "") => {
  const match = address.trim().match(/^(.*?)(?:\s+(\d+)(?:\s*([A-Za-z0-9-]+))?)$/);
  return {
    street: match?.[1] || address,
    houseNumber: match?.[2] || "",
    houseAddition: match?.[3] || "",
  };
};

export default function DutchAddressFields({
  address,
  zipCode,
  city,
  onChange,
  onAddressFound,
  t,
  inputClassName,
  labelClassName,
  fieldClassName = "",
  addressClassName = "",
}: DutchAddressFieldsProps) {
  const initial = useRef(splitStreetAddress(address));
  const onChangeRef = useRef(onChange);
  const onAddressFoundRef = useRef(onAddressFound);
  const [houseNumber, setHouseNumber] = useState(initial.current.houseNumber);
  const [houseAddition, setHouseAddition] = useState(initial.current.houseAddition);
  const [status, setStatus] = useState<"idle" | "loading" | "not-found" | "technical">("idle");

  useEffect(() => {
    onChangeRef.current = onChange;
    onAddressFoundRef.current = onAddressFound;
  }, [onAddressFound, onChange]);

  useEffect(() => {
    const postal = zipCode.trim();
    if (!isValidDutchPostalCode(postal) || !isValidDutchHouseNumber(houseNumber)) {
      setStatus("idle");
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setStatus("loading");
      try {
        const found = await lookupDutchAddress(postal, houseNumber, houseAddition, controller.signal);
        if (!found) {
          setStatus("not-found");
          return;
        }
        if (onAddressFoundRef.current) {
          onAddressFoundRef.current({ address: found.address, zipCode: found.zipCode, city: found.city });
        }
        else {
          onChangeRef.current("address", found.address);
          onChangeRef.current("zipCode", found.zipCode);
          onChangeRef.current("city", found.city);
        }
        setStatus("idle");
      } catch (error) {
        if ((error as Error)?.name !== "AbortError") setStatus("technical");
      }
    }, 350);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [houseAddition, houseNumber, zipCode]);

  const clearResolvedAddress = () => {
    if (address) onChange("address", "");
    if (city) onChange("city", "");
  };

  const field = (label: string, child: React.ReactNode, className = "") => (
    <label className={`${labelClassName} ${fieldClassName} ${className}`.trim()}>
      <span>{label}</span>
      {child}
    </label>
  );

  return (
    <>
      {field(t("family.common.zip_code", { defaultValue: "Postal code" }), (
        <input
          name="zipCode"
          value={zipCode}
          onChange={(event) => {
            clearResolvedAddress();
            onChange("zipCode", event.target.value.toUpperCase());
          }}
          autoComplete="postal-code"
          className={inputClassName}
        />
      ))}
      {field(t("family.common.house_number", { defaultValue: "House number" }), (
        <input
          name="houseNumber"
          inputMode="numeric"
          value={houseNumber}
          onChange={(event) => {
            clearResolvedAddress();
            setHouseNumber(event.target.value);
          }}
          autoComplete="address-line2"
          className={inputClassName}
        />
      ))}
      {field(t("family.common.house_number_addition", { defaultValue: "Addition" }), (
        <input
          name="houseAddition"
          value={houseAddition}
          onChange={(event) => {
            clearResolvedAddress();
            setHouseAddition(event.target.value);
          }}
          className={inputClassName}
        />
      ))}
      {field(t("family.common.address", { defaultValue: "Address" }), (
        <input name="address" value={address} onChange={(event) => onChange("address", event.target.value)} autoComplete="street-address" className={inputClassName} />
      ), addressClassName)}
      {field(t("family.common.city", { defaultValue: "City" }), (
        <input name="city" value={city} onChange={(event) => onChange("city", event.target.value)} autoComplete="address-level2" className={inputClassName} />
      ))}
      {status !== "idle" && (
        <p className={`text-xs font-semibold ${status === "loading" ? "text-slate-500 dark:text-slate-300" : "text-red-600 dark:text-red-300"}`} role="status">
          {status === "loading"
            ? t("address_lookup.loading", { defaultValue: "Looking up address..." })
            : status === "not-found"
              ? t("address_lookup.not_found", { defaultValue: "Address not found. Check the postal code and house number." })
              : t("address_lookup.unavailable", { defaultValue: "Address lookup is currently unavailable. You can enter the address manually." })}
        </p>
      )}
    </>
  );
}
